import { createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  const parseCachedJson = (key, fallback = {}) => {
    const cached = localStorage.getItem(key);
    if (!cached) {
      return fallback;
    }

    try {
      return JSON.parse(cached);
    } catch (error) {
      console.warn(`Invalid cached data for ${key}`, error);
      localStorage.removeItem(key);
      return fallback;
    }
  };

  const [authTokens, setAuthTokens] = useState(() =>
    localStorage.getItem("token") ? localStorage.getItem("token") : null,
  );
  const [user, setUser] = useState(() => parseCachedJson("user", {}));
  const [userSettings, setUserSettings] = useState(() => parseCachedJson("userSettings", {}));

  const [loading, setLoading] = useState(() => {
    const token = localStorage.getItem("token");
    const cachedUser = parseCachedJson("user", null);
    const cachedSettings = parseCachedJson("userSettings", null);
    const hasCachedAuthData = Boolean(cachedUser || cachedSettings);

    if (!token) {
      return false;
    }

    return !hasCachedAuthData;
  });

  const history = useNavigate();

  const fetchUser = async (token = authTokens) => {
    if (!token) {
      return false;
    }

    try {
      const response = await fetch(process.env.REACT_APP_API_URL + "api/v1/users/user/", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
      });

      if (response.status === 200) {
        const data = await response.json();
        setUser(data);
        localStorage.setItem("user", JSON.stringify(data));
        return true;
      }

      console.log(response);
      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const fetchUserSettings = async (token = authTokens) => {
    if (!token) {
      return false;
    }

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + "api/v1/frontend/frontend-settings/",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        },
      );

      if (response.status === 200) {
        const data = await response.json();
        setUserSettings(data);
        localStorage.setItem("userSettings", JSON.stringify(data));
        return true;
      }

      console.log(response);
      return false;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  useEffect(() => {
    const loadAuthData = async () => {
      if (!authTokens) {
        setUser({});
        setUserSettings({});
        setLoading(false);
        return;
      }

      const cachedUser = parseCachedJson("user", null);
      const cachedSettings = parseCachedJson("userSettings", null);
      const hasCachedAuthData = Boolean(cachedUser || cachedSettings);

      if (hasCachedAuthData) {
        if (cachedUser) {
          setUser(cachedUser);
        }
        if (cachedSettings) {
          setUserSettings(cachedSettings);
        }
        setLoading(false);
      } else {
        setLoading(true);
      }

      const [userOk, settingsOk] = await Promise.all([
        fetchUser(authTokens),
        fetchUserSettings(authTokens),
      ]);

      if (!userOk || !settingsOk) {
        setAuthTokens(null);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("userSettings");
        setUser({});
        setUserSettings({});
        setLoading(false);
        history("/login");
        return;
      }

      setLoading(false);
    };

    loadAuthData();
  }, [authTokens]);

  const loginUser = async (email, password) => {
    const response = await fetch(process.env.REACT_APP_API_URL + "api/v1/users/auth/login/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password,
      }),
    });
    const data = await response.json();

    if (response.status === 200) {
      setAuthTokens(data.key);
      localStorage.setItem("token", data.key);

      if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "auth_token", token: data.key }),
        );
        return data;
      }

      history("/");
      return data;
    }

    return data;
  };

  const registerUser = async (
    email,
    first_name,
    last_name,
    organization_name,
    activation_key,
    password1,
    password2,
  ) => {
    const response = await fetch(process.env.REACT_APP_API_URL + "api/v1/users/auth/register/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        first_name,
        last_name,
        organization_name,
        activation_key,
        password1,
        password2,
      }),
    });
    if (response.status === 201) {
      setAuthTokens(response.data);
      history("/login");
      return response;
    }

    return response;
  };

  const logoutUser = () => {
    setAuthTokens(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("userSettings");
    history("/login");
  };

  const contextData = {
    user,
    userSettings,
    authTokens,
    setAuthTokens,
    registerUser,
    loginUser,
    logoutUser,
  };

  return (
    <AuthContext.Provider value={contextData}>{loading ? null : children}</AuthContext.Provider>
  );
};
